export const HANDOFF_PURPOSES = [
   'payment',
   'fub-connection',
   'calendar-connection',
] as const
export type HandoffPurpose = (typeof HANDOFF_PURPOSES)[number]

export const HANDOFF_TITLES: Record<HandoffPurpose, string> = {
   payment: 'Complete your payment',
   'fub-connection': 'Connect Follow Up Boss',
   'calendar-connection': 'Connect your calendar',
}

export interface Refusal {
   reason: string
   message: string
   /** Shown only when the reader has to do something other than ask again. */
   guidance?: string
}

/**
 * Why the link did not open, in words that say what to do next.
 *
 * Four outcomes, deliberately not one. "Expired" invites a fresh link,
 * "already used" means a reopened tab, "wrong person" means this browser is
 * signed in as somebody else and needs an account switch, and anything else
 * is unknown. A single "invalid link" would hide the only one the reader can
 * actually fix.
 */
export const describeRefusal = (
   reason: string | undefined,
   message: string | undefined,
): Refusal => {
   const fallback =
      'This link is no longer valid. Ask for a new one from your conversation.'
   switch (reason) {
      case 'wrong-person':
         return {
            reason,
            message:
               message ??
               'This link was created for a different account.',
            guidance:
               'Sign out and sign in as the person who started this, then open the link again.',
         }
      case 'already-redeemed':
         return {
            reason,
            message:
               message ??
               'This link has already been used. Ask for a new one from your conversation.',
         }
      case 'expired':
         return { reason, message: message ?? fallback }
      case 'not-loaded':
         return {
            reason,
            message:
               message ??
               'Your setup could not be loaded, so this page will not act on it.',
            guidance: 'Try the link again from your conversation in a moment.',
         }
      case 'missing':
         return {
            reason,
            message:
               'This link is incomplete. Ask for a new one from your conversation.',
         }
      default:
         return { reason: reason ?? 'unknown', message: message ?? fallback }
   }
}
